import { Injectable, inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { switchMap, withLatestFrom, map, catchError, of } from "rxjs";
import {
    ContractClient,
    ContractDiscountClient,
    ContractFileClient,
    ContractReminderClient,
    ContractSectionClient,
    FileParameter,
    UpdateContractRequest,
} from "src/api-service";
import { selectSelectedCompany } from "../../user/user.selectors";
import { ContractActions } from "./contract.actions";
import { normalizeError } from "../../app-store-error.types";
import { selectContractState } from "./contract.selectors";

// **************************************************************************
// CONTRACT EFFECTS (detail only - list effects are in SuperUserEffects)
// **************************************************************************

@Injectable()
export class ContractEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly contractClient = inject(ContractClient);
    private readonly sectionClient = inject(ContractSectionClient);
    private readonly fileClient = inject(ContractFileClient);
    private readonly reminderClient = inject(ContractReminderClient);
    private readonly discountClient = inject(ContractDiscountClient);

    // *** LOAD CONTRACT (DETAIL) ***
    loadContract$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ContractActions.loadContract),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ id }, selectedCompany]) =>
                this.contractClient.get(id, selectedCompany!.id).pipe(
                    map(contract =>
                        ContractActions.loadContractSuccess({ contract }),
                    ),
                    catchError(error =>
                        of(ContractActions.loadContractFailure({
                            error: normalizeError(error),
                            toastKey: "ADMIN.SUPER_USER.CONTRACTS.TOAST.LOAD_ERROR",
                        })),
                    ),
                ),
            ),
        ),
    );

    // *** UPDATE CONTRACT ***
    updateContract$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ContractActions.updateContract),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ id, request }, selectedCompany]) =>
                this.contractClient.update(id, request, selectedCompany!.id).pipe(
                    map(contract =>
                        ContractActions.updateContractSuccess({
                            contract,
                            toastKey: "ADMIN.SUPER_USER.CONTRACTS.TOAST.UPDATE_SUCCESS",
                        }),
                    ),
                    catchError(error =>
                        of(ContractActions.updateContractFailure({
                            error: normalizeError(error),
                            toastKey: "ADMIN.SUPER_USER.CONTRACTS.TOAST.UPDATE_ERROR",
                        })),
                    ),
                ),
            ),
        ),
    );

    // *** CREATE SECTION ***
    createSection$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ContractActions.createSection),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ contractId, request }, selectedCompany]) =>
                this.sectionClient.create(selectedCompany!.id, request).pipe(
                    map(section =>
                        ContractActions.createSectionSuccess({
                            section,
                            toastKey: "ADMIN.SUPER_USER.CONTRACTS.TOAST.SECTION_CREATE_SUCCESS",
                        }),
                    ),
                    catchError(error =>
                        of(ContractActions.createSectionFailure({
                            error: normalizeError(error),
                            toastKey: "ADMIN.SUPER_USER.CONTRACTS.TOAST.SECTION_CREATE_ERROR",
                        })),
                    ),
                ),
            ),
        ),
    );

    // *** UPDATE SECTION ***
    updateSection$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ContractActions.updateSection),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ sectionId, request }, selectedCompany]) =>
                this.sectionClient.update(sectionId, request, selectedCompany!.id).pipe(
                    map(section =>
                        ContractActions.updateSectionSuccess({
                            section,
                            toastKey: "ADMIN.SUPER_USER.CONTRACTS.TOAST.SECTION_UPDATE_SUCCESS",
                        }),
                    ),
                    catchError(error =>
                        of(ContractActions.updateSectionFailure({
                            error: normalizeError(error),
                            toastKey: "ADMIN.SUPER_USER.CONTRACTS.TOAST.SECTION_UPDATE_ERROR",
                        })),
                    ),
                ),
            ),
        ),
    );

    // *** DELETE SECTION ***
    deleteSection$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ContractActions.deleteSection),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ sectionId }, selectedCompany]) =>
                this.sectionClient.delete(sectionId, selectedCompany!.id).pipe(
                    map(() =>
                        ContractActions.deleteSectionSuccess({
                            sectionId,
                            toastKey: "ADMIN.SUPER_USER.CONTRACTS.TOAST.SECTION_DELETE_SUCCESS",
                        }),
                    ),
                    catchError(error =>
                        of(ContractActions.deleteSectionFailure({
                            error: normalizeError(error),
                            toastKey: "ADMIN.SUPER_USER.CONTRACTS.TOAST.SECTION_DELETE_ERROR",
                        })),
                    ),
                ),
            ),
        ),
    );

    // *** SET SECTION CATALOGS ***
    setSectionCatalogs$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ContractActions.setSectionCatalogs),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ sectionId, request }, selectedCompany]) =>
                this.sectionClient.setCatalogs(sectionId, request, selectedCompany!.id).pipe(
                    map(section =>
                        ContractActions.setSectionCatalogsSuccess({
                            section,
                            toastKey: "ADMIN.SUPER_USER.CONTRACTS.TOAST.CATALOGS_UPDATE_SUCCESS",
                        }),
                    ),
                    catchError(error =>
                        of(ContractActions.setSectionCatalogsFailure({
                            error: normalizeError(error),
                            toastKey: "ADMIN.SUPER_USER.CONTRACTS.TOAST.CATALOGS_UPDATE_ERROR",
                        })),
                    ),
                ),
            ),
        ),
    );

    // *** UPLOAD FILES ***
    uploadFiles$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ContractActions.uploadFiles),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ contractId, files }, selectedCompany]) => {
                const fileParams: FileParameter[] = files.map(file => ({
                    data: file,
                    fileName: file.name,
                }));

                return this.fileClient.uploadFiles(selectedCompany!.id, contractId, fileParams).pipe(
                    map(uploadedFiles =>
                        ContractActions.uploadFilesSuccess({
                            files: uploadedFiles,
                            toastKey: "ADMIN.SUPER_USER.CONTRACTS.TOAST.FILE_UPLOAD_SUCCESS",
                        }),
                    ),
                    catchError(error =>
                        of(ContractActions.uploadFilesFailure({
                            error: normalizeError(error),
                            toastKey: "ADMIN.SUPER_USER.CONTRACTS.TOAST.FILE_UPLOAD_ERROR",
                        })),
                    ),
                );
            }),
        ),
    );

    // *** DELETE FILE ***
    deleteFile$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ContractActions.deleteFile),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ contractId, fileId }, selectedCompany]) =>
                this.fileClient.deleteFile(contractId, fileId, selectedCompany!.id).pipe(
                    map(() =>
                        ContractActions.deleteFileSuccess({
                            fileId,
                            toastKey: "ADMIN.SUPER_USER.CONTRACTS.TOAST.FILE_DELETE_SUCCESS",
                        }),
                    ),
                    catchError(error =>
                        of(ContractActions.deleteFileFailure({
                            error: normalizeError(error),
                            toastKey: "ADMIN.SUPER_USER.CONTRACTS.TOAST.FILE_DELETE_ERROR",
                        })),
                    ),
                ),
            ),
        ),
    );

    // *** LOAD REMINDERS ***
    loadReminders$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ContractActions.loadReminders),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ contractId }, selectedCompany]) =>
                this.reminderClient.getByContractId(contractId, selectedCompany!.id).pipe(
                    map(reminders =>
                        ContractActions.loadRemindersSuccess({ reminders }),
                    ),
                    catchError(error =>
                        of(ContractActions.loadRemindersFailure({
                            error: normalizeError(error),
                            toastKey: "CONTRACT.REMINDERS.TOAST.LOAD_ERROR",
                        })),
                    ),
                ),
            ),
        ),
    );

    // *** CREATE REMINDER ***
    createReminder$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ContractActions.createReminder),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ request }, selectedCompany]) =>
                this.reminderClient.create(selectedCompany!.id, request).pipe(
                    map(reminder =>
                        ContractActions.createReminderSuccess({
                            reminder,
                            toastKey: "CONTRACT.REMINDERS.TOAST.CREATE_SUCCESS",
                        }),
                    ),
                    catchError(error =>
                        of(ContractActions.createReminderFailure({
                            error: normalizeError(error),
                            toastKey: "CONTRACT.REMINDERS.TOAST.CREATE_ERROR",
                        })),
                    ),
                ),
            ),
        ),
    );

    // *** UPDATE REMINDER ***
    updateReminder$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ContractActions.updateReminder),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ id, request }, selectedCompany]) =>
                this.reminderClient.update(id, request, selectedCompany!.id).pipe(
                    map(reminder =>
                        ContractActions.updateReminderSuccess({
                            reminder,
                            toastKey: "CONTRACT.REMINDERS.TOAST.UPDATE_SUCCESS",
                        }),
                    ),
                    catchError(error =>
                        of(ContractActions.updateReminderFailure({
                            error: normalizeError(error),
                            toastKey: "CONTRACT.REMINDERS.TOAST.UPDATE_ERROR",
                        })),
                    ),
                ),
            ),
        ),
    );

    // *** DELETE REMINDER ***
    deleteReminder$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ContractActions.deleteReminder),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ contractId, reminderId }, selectedCompany]) =>
                this.reminderClient.delete(reminderId, selectedCompany!.id).pipe(
                    map(() =>
                        ContractActions.deleteReminderSuccess({
                            reminderId,
                            toastKey: "CONTRACT.REMINDERS.TOAST.DELETE_SUCCESS",
                        }),
                    ),
                    catchError(error =>
                        of(
                            ContractActions.deleteReminderFailure({
                                error: normalizeError(error),
                                toastKey: "CONTRACT.REMINDERS.TOAST.DELETE_ERROR",
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    // *** SET DISCOUNT TYPE ***
    setDiscountType$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ContractActions.setDiscountType),
            withLatestFrom(this.store.select(selectSelectedCompany), this.store.select(selectContractState)),
            switchMap(([{ contractId, discountType }, selectedCompany, contractState]) => {
                const contract = contractState.contract;
                const request: UpdateContractRequest = {
                    contractName: contract?.contractName,
                    startDate: contract?.startDate,
                    endDate: contract?.endDate,
                    notes: contract?.notes,
                    shippingAndAssemblyTerms: contract?.shippingAndAssemblyTerms,
                    serviceTerms: contract?.serviceTerms,
                    contactPersonName: contract?.contactPersonName,
                    contactPersonRole: contract?.contactPersonRole,
                    contactPersonPhone: contract?.contactPersonPhone,
                    contactPersonEmail: contract?.contactPersonEmail,
                    discountType: discountType,
                    excludeEnvironmentalFee: contract?.excludeEnvironmentalFee ?? false,
                    excludeProjectManagement: contract?.excludeProjectManagement ?? false,
                    defaultPaymentTermCode: contract?.defaultPaymentTermCode,
                    countryCode: contract?.countryCode,
                };
                return this.contractClient.update(contractId, request, selectedCompany!.id).pipe(
                    map(contract =>
                        ContractActions.setDiscountTypeSuccess({
                            contract,
                            toastKey: "CONTRACT.DISCOUNTS.TOAST.MODE_SUCCESS",
                        }),
                    ),
                    catchError(error =>
                        of(
                            ContractActions.setDiscountTypeFailure({
                                error: normalizeError(error),
                                toastKey: "CONTRACT.DISCOUNTS.TOAST.MODE_ERROR",
                            }),
                        ),
                    ),
                );
            }),
        ),
    );

    // *** CREATE DISCOUNT ***
    createDiscount$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ContractActions.createDiscount),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ request }, selectedCompany]) =>
                this.discountClient.create(selectedCompany!.id, request).pipe(
                    map(discount =>
                        ContractActions.createDiscountSuccess({
                            discount,
                            toastKey: "CONTRACT.DISCOUNTS.TOAST.ADD_SUCCESS",
                        }),
                    ),
                    catchError(error =>
                        of(
                            ContractActions.createDiscountFailure({
                                error: normalizeError(error),
                                toastKey: "CONTRACT.DISCOUNTS.TOAST.ADD_ERROR",
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    // *** UPDATE DISCOUNT ***
    updateDiscount$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ContractActions.updateDiscount),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ id, request }, selectedCompany]) =>
                this.discountClient.update(id, request, selectedCompany!.id).pipe(
                    map(discount =>
                        ContractActions.updateDiscountSuccess({
                            discount,
                            toastKey: "CONTRACT.DISCOUNTS.TOAST.UPDATE_SUCCESS",
                        }),
                    ),
                    catchError(error =>
                        of(
                            ContractActions.updateDiscountFailure({
                                error: normalizeError(error),
                                toastKey: "CONTRACT.DISCOUNTS.TOAST.UPDATE_ERROR",
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    // *** DELETE DISCOUNT ***
    deleteDiscount$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ContractActions.deleteDiscount),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ discountId }, selectedCompany]) =>
                this.discountClient.delete(discountId, selectedCompany!.id).pipe(
                    map(() =>
                        ContractActions.deleteDiscountSuccess({
                            discountId,
                            toastKey: "CONTRACT.DISCOUNTS.TOAST.DELETE_SUCCESS",
                        }),
                    ),
                    catchError(error =>
                        of(
                            ContractActions.deleteDiscountFailure({
                                error: normalizeError(error),
                                toastKey: "CONTRACT.DISCOUNTS.TOAST.DELETE_ERROR",
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );
}
